import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TimerProps {
  duration: number;
  onTimeUp: () => void;
  isActive: boolean;
}

export function InterviewTimer({ duration, onTimeUp, isActive }: TimerProps) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const { t } = useTranslation();

  // Reset timer when duration changes or when isActive changes to true
  useEffect(() => {
    if (isActive) {
      setTimeLeft(duration);
    }
  }, [duration, isActive]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isActive && timeLeft > 0) {
      const startTime = Date.now();
      const endTime = startTime + (timeLeft * 1000);

      timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.ceil((endTime - now) / 1000);

        if (remaining <= 0) {
          clearInterval(timer);
          setTimeLeft(0);
          onTimeUp();
        } else {
          setTimeLeft(remaining);
        }
      }, 100);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isActive, timeLeft, onTimeUp]);

  const progress = (timeLeft / duration) * 100;
  const isWarning = timeLeft <= 10;
  const isCritical = timeLeft <= 5;

  return (
    <div className="w-full space-y-2 bg-gray-800 bg-opacity-75 p-4 rounded-lg">
      <div className="flex justify-between items-center">
        <span className={`text-sm font-medium ${isWarning ? 'text-red-400' : 'text-gray-200'}`}>
          {t('interview.timer.timeLeft', { seconds: timeLeft })}
        </span>
        {isWarning && (
          <span className={`text-sm font-medium ${isCritical ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>
            {t('interview.timer.almostUp')}
          </span>
        )}
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-100 ${
            isCritical ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}